import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Mail, Phone } from "lucide-react";
import { MaskLine, Rise } from "@/components/motion/Reveal";
import { PracticeFaq } from "@/components/practice/PracticeFaq";
import { DIRECT_EMAIL, DIRECT_PHONE, DIRECT_PHONE_TEL } from "@/components/layout/ConsultPopover";
import { PRACTICE_AREAS } from "@/data/site";
import { PROFILES } from "@/data/profiles";
import { getPracticeDetail } from "@/data/practiceDetails";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/seo/Seo";
import { ldBreadcrumb, ldFaq, ldService } from "@/seo/seoConfig";

function Eyebrow({ children }) {
  return (
    <Rise>
      <div className="flex items-center gap-4 mb-10"><span className="accent-bar" /><span className="eyebrow">{children}</span></div>
    </Rise>
  );
}

export default function PracticeDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const d = getPracticeDetail(slug);

  useEffect(() => {
    if (!d) navigate("/practice-areas", { replace: true });
  }, [d, navigate]);

  if (!d) return null;

  const { area } = d;
  const path = `/practice-areas/${area.slug}`;
  const partner = PROFILES.find((p) => p.slug === d.partner);
  const related = d.related.map((s) => PRACTICE_AREAS.find((p) => p.slug === s)).filter(Boolean);
  const pd = t.practiceDetail;
  const jsonLd = [
    ldService(area, path, d.seoDescription),
    ldBreadcrumb([{ name: "Home", path: "/" }, { name: "Practice Areas", path: "/practice-areas" }, { name: area.title, path }]),
  ];
  if (d.faqs.length) jsonLd.push(ldFaq(d.faqs));

  return (
    <div className="pt-40" data-testid={`practice-detail-${area.slug}`}>
      <Seo title={d.seoTitle} description={d.seoDescription} path={path} keywords={d.keywords} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Rise>
          <Link to="/practice-areas" data-testid="practice-detail-back" className="chapter-num inline-flex items-center gap-2 text-[#A19D94] hover:text-[#F4F1EB] transition-colors">
            <ArrowLeft size={14} strokeWidth={1.25} /> {pd.back}
          </Link>
        </Rise>
        <div className="mt-12 flex flex-wrap items-center gap-3">
          <span className={`chapter-num ${area.featured ? "text-[#C84C35]" : "text-[#66635C]"}`}>{area.n} / 15</span>
          {area.featured && <span data-testid="practice-detail-featured" className="chapter-num bg-[#C84C35] text-[#050505] px-2.5 py-1">{t.practice.featured}</span>}
          {area.tags.map((tag) => <span key={tag} className="chapter-num border border-[#222] px-2.5 py-1">{tag}</span>)}
        </div>
        <h1 className="font-serif tracking-tight leading-[0.9] text-[12vw] lg:text-[7.5vw] mt-8">
          <MaskLine delay={0.15}>{d.h1a}</MaskLine>
          {d.h1b && <MaskLine delay={0.32}><span className="italic-accent">{d.h1b}</span></MaskLine>}
        </h1>
        <Rise delay={0.5}>
          <p data-testid="practice-detail-lead" className="mt-10 text-[#A19D94] text-lg lg:text-xl max-w-3xl leading-relaxed">{d.lead}</p>
        </Rise>
        <Rise delay={0.6}>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="practice-detail-hero-cta" className="btn-primary">{pd.cta} <ArrowUpRight size={14} strokeWidth={1.5} /></Link>
            <a href={`tel:${DIRECT_PHONE_TEL}`} className="btn-ghost" data-testid="practice-detail-hero-phone"><Phone size={14} strokeWidth={1.5} /> {DIRECT_PHONE}</a>
          </div>
        </Rise>
      </section>

      {/* OVERVIEW + PARTNER */}
      <section className="mt-28 border-y border-[#141414] py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <Eyebrow>{d.howWeWork ? pd.overview : pd.howWeWork}</Eyebrow>
            <div className="space-y-7">
              {d.intro.map((p, i) => (
                <Rise key={i} delay={i * 0.05}>
                  <p className={`leading-relaxed ${i === 0 ? "font-serif text-2xl lg:text-3xl text-[#F4F1EB]" : "text-[#A19D94] text-base lg:text-lg"}`}>{p}</p>
                </Rise>
              ))}
              {d.howWeWork && (
                <Rise delay={0.1}>
                  <div className="mt-12 border-l-2 border-[#C84C35] pl-6">
                    <div className="eyebrow mb-3">{pd.howWeWork}</div>
                    <p className="text-[#A19D94] leading-relaxed">{d.howWeWork}</p>
                  </div>
                </Rise>
              )}
            </div>
          </div>
          {partner && (
            <div className="lg:col-span-4 lg:col-start-9">
              <Eyebrow>{pd.lead}</Eyebrow>
              <Rise delay={0.1}>
                <Link to={`/team/${partner.slug}`} data-testid="practice-detail-partner" className="block group">
                  <div className="overflow-hidden">
                    <img src={partner.img} alt={partner.name} className="w-full h-[420px] object-cover object-top photo-mono group-hover:scale-105 transition-transform duration-1000 ease-out" />
                  </div>
                  <div className="font-serif text-3xl mt-6 group-hover:text-[#C84C35] transition-colors duration-500">{partner.name}</div>
                  <div className="text-[#A19D94] text-sm mt-1">{partner.role}</div>
                  <div className="chapter-num text-[#66635C] mt-3">{partner.focus}</div>
                  <div className="chapter-num mt-6 inline-flex items-center gap-2 link-hover">{pd.profile} <ArrowUpRight size={12} strokeWidth={1.5} /></div>
                </Link>
              </Rise>
            </div>
          )}
        </div>
      </section>

      {/* SERVICES */}
      {d.services.length > 0 && (
        <section className="py-24 lg:py-32" data-testid="practice-detail-services">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <Eyebrow>{pd.services} · {String(d.services.length).padStart(2, "0")}</Eyebrow>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#141414] border border-[#141414]">
              {d.services.map((s, i) => (
                <Rise key={s.t} delay={i * 0.04} className="bg-[#050505]">
                  <div className="p-8 h-full" data-testid={`practice-service-${i + 1}`}>
                    <div className="chapter-num text-[#C84C35]">§{String(i + 1).padStart(2, "0")}</div>
                    <div className="font-serif text-2xl mt-4 leading-tight">{s.t}</div>
                    <p className="mt-4 text-[#A19D94] text-sm leading-relaxed">{s.d}</p>
                  </div>
                </Rise>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MATTERS */}
      {d.matters.length > 0 && (
        <section className="py-24 border-t border-[#141414]" data-testid="practice-detail-matters">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <Eyebrow>{pd.matters}</Eyebrow>
                <Rise delay={0.1}><h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight">{t.matters.title1} <span className="italic-accent">{t.matters.title2}</span></h2></Rise>
              </div>
              <Rise delay={0.15}><p className="chapter-num text-[#66635C] max-w-sm">{pd.mattersNote}</p></Rise>
            </div>
            <div className="border-t border-[#141414]">
              {d.matters.map((m, i) => (
                <Rise key={m.title} delay={i * 0.05}>
                  <div className="grid grid-cols-12 gap-4 items-start py-10 border-b border-[#141414]" data-testid={`practice-matter-${i + 1}`}>
                    <div className="col-span-2 lg:col-span-1 chapter-num pt-2">{String(i + 1).padStart(2, "0")}</div>
                    <div className="col-span-10 lg:col-span-2 chapter-num"><div className="text-[#66635C]">{m.year}</div><div className="mt-2 text-[#C84C35]">{m.tag}</div></div>
                    <div className="col-span-12 lg:col-span-6">
                      <h3 className="font-serif text-2xl lg:text-4xl leading-[1.05] tracking-tight">{m.title}</h3>
                      <p className="mt-4 text-[#A19D94] leading-relaxed">{m.body}</p>
                    </div>
                    <div className="col-span-12 lg:col-span-3 flex lg:justify-end"><span className="font-serif text-2xl lg:text-3xl italic-accent">{m.value}</span></div>
                  </div>
                </Rise>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {d.faqs.length > 0 && (
        <section className="py-24 lg:py-32 border-t border-[#141414]" data-testid="practice-detail-faqs">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>{pd.faqs}</Eyebrow>
              <Rise delay={0.1}><h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight">{area.title.split(" ")[0]} <span className="italic-accent">{area.title.split(" ").slice(1).join(" ")}</span></h2></Rise>
            </div>
            <div className="lg:col-span-8"><PracticeFaq faqs={d.faqs} /></div>
          </div>
        </section>
      )}

      {/* RELATED READING */}
      {d.insights.length > 0 && (
        <section className="py-24 border-t border-[#141414]" data-testid="practice-detail-insights">
          <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
              <div>
                <Eyebrow>{pd.reading}</Eyebrow>
                <Rise delay={0.1}><h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight">{t.insights.title1} <span className="italic-accent">{t.insights.title2}</span></h2></Rise>
              </div>
              <Rise delay={0.15}><Link to="/insights" data-testid="practice-insights-all" className="btn-ghost">{t.insights.all} <ArrowUpRight size={14} strokeWidth={1.5} /></Link></Rise>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#141414] border border-[#141414]">
              {d.insights.map((n, i) => (
                <Rise key={n.slug} delay={i * 0.06} className="bg-[#050505]">
                  <Link to={`/insights/${n.slug}`} data-testid={`practice-insight-${n.slug}`} className="block p-8 lg:p-10 h-full flex flex-col justify-between hover:bg-[#0A0A0A] transition-colors duration-500 group">
                    <div>
                      <div className="flex items-center justify-between chapter-num">
                        <span className="text-[#C84C35]">{n.cat}</span>
                        <span>{n.read}</span>
                      </div>
                      <h3 className="mt-8 font-serif text-2xl lg:text-3xl leading-tight group-hover:text-[#C84C35] transition-colors duration-700">{n.title}</h3>
                      <p className="mt-5 text-[#A19D94] text-sm leading-relaxed">{n.dek}</p>
                    </div>
                    <div className="mt-8 pt-6 border-t border-[#141414] flex items-center justify-between gap-4 text-sm">
                      <span className="text-[#A19D94]">{n.author} · {n.date}</span>
                      <span className="chapter-num inline-flex items-center gap-2 text-[#F4F1EB] shrink-0 whitespace-nowrap">{t.insights.read} <ArrowUpRight size={12} strokeWidth={1.5} /></span>
                    </div>
                  </Link>
                </Rise>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* RELATED */}
      <section className="py-24 border-t border-[#141414]" data-testid="practice-detail-related">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Eyebrow>{pd.related}</Eyebrow>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#141414] border border-[#141414]">
            {related.map((r, i) => (
              <Rise key={r.slug} delay={i * 0.05} className="bg-[#050505]">
                <Link to={`/practice-areas/${r.slug}`} data-testid={`practice-related-${r.slug}`} className="block p-8 h-full group">
                  <div className="chapter-num text-[#66635C]">{r.n}</div>
                  <div className="font-serif text-2xl lg:text-3xl mt-4 leading-tight group-hover:text-[#C84C35] transition-colors duration-500">{r.title}</div>
                  <div className="chapter-num mt-6 inline-flex items-center gap-2 text-[#A19D94]">{pd.explore} <ArrowUpRight size={12} strokeWidth={1.5} /></div>
                </Link>
              </Rise>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 lg:py-40 border-t border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Rise><h2 className="font-serif text-5xl lg:text-[6vw] leading-[0.95] tracking-tight">{pd.ctaTitle1} <span className="italic-accent">{pd.ctaTitle2}</span></h2></Rise>
            <Rise delay={0.1}><p className="mt-8 text-[#A19D94] text-lg max-w-2xl leading-relaxed">{pd.ctaLead}</p></Rise>
          </div>
          <div className="lg:col-span-5 flex flex-col items-start lg:items-end gap-6">
            <Rise delay={0.15}><Link to="/contact" data-testid="practice-detail-cta" className="btn-primary">{pd.cta} <ArrowUpRight size={14} strokeWidth={1.5} /></Link></Rise>
            <Rise delay={0.2}>
              <div className="flex flex-col lg:items-end gap-2 text-sm">
                <div className="eyebrow">{pd.direct}</div>
                <a href={`mailto:${DIRECT_EMAIL}`} data-testid="practice-detail-email" className="inline-flex items-center gap-2 text-[#F4F1EB] link-hover"><Mail size={14} strokeWidth={1.25} /> {DIRECT_EMAIL}</a>
                <a href={`tel:${DIRECT_PHONE_TEL}`} data-testid="practice-detail-phone" className="inline-flex items-center gap-2 text-[#F4F1EB] link-hover"><Phone size={14} strokeWidth={1.25} /> {DIRECT_PHONE}</a>
              </div>
            </Rise>
          </div>
        </div>
      </section>
    </div>
  );
}
